"use client";

import { ChevronsLeftRightEllipsis, GalleryHorizontal } from "lucide-react";
import { Label } from "@/components/ui/label";
import { useStoreBuilderContext } from "@/ui/admin/store-builder/store-builder-context";
import { debounce, useDebounce } from "@/ui/hooks/use-debounced-value";
import { useRouterRefresh } from "@/ui/hooks/use-router-refresh";
import { StatefulSlider } from "@/ui/stateful-slider";
import { TailwindColorPicker } from "@/ui/tailwind-color-picker";
import { useStartGlobalTransition } from "@/zustand/admin";
import { CompactButtonGroup } from "../../shared/compact-button-group";
import type { YnsComponentForm } from "../types";
import type { DividerStoreBuilderConfigFactory } from "./config";
import type { Divider } from "./divider";

export const DividerForm: YnsComponentForm<typeof DividerStoreBuilderConfigFactory> = ({ data, theme }) => {
	const context = useStoreBuilderContext<typeof Divider>();
	const refresh = useRouterRefresh();
	const startGlobalTransition = useStartGlobalTransition();

	const handleHeightChange = useDebounce(async (v: number[]) => {
		return startGlobalTransition(async () => {
			await context.saveSelectedPageSectionField({
				fieldPath: "height",
				value: v[0],
			});
			await refresh();
		});
	}, 200);
	const handleMarginChange = useDebounce(async (v: number[]) => {
		return startGlobalTransition(async () => {
			await context.saveSelectedPageSectionField({
				fieldPath: "margin",
				value: v[0],
			});
			await refresh();
		});
	}, 200);

	return (
		<div className="space-y-4">
			<Label className="grid gap-1">
				<span className="text-xs text-neutral-600 font-medium">Line height</span>
				<StatefulSlider
					defaultValue={[data.height ?? 1]}
					min={1}
					max={24}
					onValueChange={handleHeightChange}
				/>
			</Label>

			<Label className="grid gap-1">
				<span className="text-xs text-neutral-600 font-medium">Line margins</span>
				<StatefulSlider
					defaultValue={[data.margin ?? 4]}
					min={1}
					max={48}
					onValueChange={handleMarginChange}
				/>
			</Label>

			<div className="grid grid-cols-2 gap-2">
				<div className="grid gap-1">
					<span className="text-xs text-neutral-600 font-medium">Size</span>
					<CompactButtonGroup
						options={[
							{ value: "constrained", icon: <GalleryHorizontal className="size-3" />, tooltip: "Fixed" },
							{ value: "full", icon: <ChevronsLeftRightEllipsis className="size-3" />, tooltip: "Full" },
						]}
						defaultValue={data?.size ?? "constrained"}
						onValueChange={(value) => {
							context.saveSelectedPageSectionField({
								fieldPath: "size",
								value,
							});
						}}
						className="w-full"
					/>
				</div>

				<Label className="grid gap-1">
					<span className="text-xs text-neutral-600 font-medium">Background Color</span>
					<TailwindColorPicker
						defaultValue={theme?.backgroundColor}
						onValueChange={debounce(
							(value) => {
								context.saveSelectedPageSectionThemeField({
									fieldPath: "backgroundColor",
									value,
								});
							},
							300,
							{ trailing: true },
						)}
					/>
				</Label>
			</div>
		</div>
	);
};
